import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { ChevronUp, Download, LogOut, UserRound, KeyRound } from "lucide-react";
import { authService, hasSalesProduct } from "../lib/auth";
import { request } from "../lib/api";
import { useToast } from "./useToast";

export function AccountMenu() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [updateBusy, setUpdateBusy] = useState(false);
  const [error, setError] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const cache = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const { data } = useQuery({ queryKey: ["auth-status"], queryFn: authService.status, retry: false });
  const { data: client } = useQuery({
    queryKey: ["installed-client-version"],
    queryFn: () => request<{ version: string | null }>("/client-update/version"),
    enabled: open, staleTime: Infinity, retry: false,
  });
  useEffect(() => {
    const close = (event: MouseEvent) => { if (!ref.current?.contains(event.target as Node)) setOpen(false); };
    const key = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close); document.addEventListener("keydown", key);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", key); };
  }, []);
  async function logout() {
    if (busy) return;
    setBusy(true); setError("");
    try { await authService.logout(); cache.clear(); navigate("/login", { replace: true }); }
    catch { setError("退出未完成，请重试；不要直接操作仍在运行的手机。"); }
    finally { setBusy(false); }
  }
  async function openUpdater() {
    if (updateBusy) return;
    setUpdateBusy(true); setError("");
    try {
      await request<{status:string}>("/client-update/start", { method: "POST" });
      toast.showToast("升级窗口已打开；如未置前，请查看桌面任务栏。");
      setOpen(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "升级窗口未能打开，请重试。");
    } finally { setUpdateBusy(false); }
  }
  return <div className="sidebar-account" ref={ref}>
    {open && <section className="sidebar-account__panel" aria-label="软件账号菜单">
      <strong>我的账号</strong>
      <small>{data?.user?.expiresAt ? `有效期至 ${new Date(data.user.expiresAt).toLocaleDateString("zh-CN")}` : "授权状态待确认"}</small>
      <Link to="/account" onClick={() => setOpen(false)}><UserRound />账号与授权</Link>
      {hasSalesProduct(data?.user) ? <Link to="/account#feedback" onClick={() => setOpen(false)}>反馈问题</Link> : null}
      <Link to="/password-reset" onClick={() => setOpen(false)}><KeyRound />修改密码</Link>
      <button type="button" disabled={updateBusy} onClick={openUpdater}><Download />{updateBusy ? "正在打开升级…" : "软件升级"}
        {client?.version && <small className="sidebar-account__version" title="当前客户端版本">v{client.version}</small>}
      </button>
      <button type="button" disabled={busy} onClick={logout}><LogOut />{busy ? "正在安全退出…" : "退出登录"}</button>
      <small>退出后暂停任务，队列和进度保留。</small>
      {error && <p role="alert">{error}</p>}
    </section>}
    <button type="button" className="sidebar-account__trigger" aria-expanded={open} aria-label="打开我的账号" onClick={() => setOpen(value => !value)}>
      <span className="sidebar-account__avatar"><UserRound /></span><span><strong>{data?.user?.maskedPhone || "我的账号"}</strong><small>{data?.user?.entitlement === "active" ? "授权有效" : "需要登录"}</small></span><ChevronUp />
    </button>
  </div>;
}
